import { AssetPlatformType } from "components/CampaignViews/AdCreativeSelectorCampaignView";
import Axios from "helpers/Interceptor"
import { FacebookCampaignSubmissionType } from "modules/Campaign";
import { MediaType } from "modules/Types";
import { TaskType } from "./maestroTasksApi";
import { AgentTaskOptions } from "./maestroTasksApi";

/** Response type for video/image content from social media posts */
export interface ImportFromURLResponse {
    id: string;
    /** Timestamp when post was created */
    timestamp: string;
    /** Direct URL to the media content */
    assetUrl: string;
    /** URL for the post thumbnail image */
    thumbnailUrl: string;
    /** Type of media content returned */
    mediaType: MediaType;
    /** Social media platform source */
    originalPostType: AssetPlatformType.INSTAGRAM | AssetPlatformType.TIKTOK;
    /** Username of the content owner */
    ownerUsername: string;
    /** Original caption text from the platform */
    platformCaption: string;
    /** Platform type of the post */
    platformType: AssetPlatformType.IMPORTED_FROM_URL;
}

/** Response type for campaign checkout simulation */
export interface CampaignCheckoutResponse {
    success: boolean;
    campaignId: string;
    status: 'PROCESSING' | 'AWAITING_PAYMENT' | 'DRAFT';
    message: string;
    taskConsumed: boolean;
}

/** Response type for campaign submission */
export interface CampaignSubmissionResponse {
    success: boolean;
    campaignId: string;
    status: string;
    message: string;
    taskConsumed: boolean;
}

export const getVideoFromUrl_api = async (url: string): Promise<ImportFromURLResponse | null> => {
    try {
        const response = await Axios.post<ImportFromURLResponse>(`/agents/get-video-from-url`, { url })
        return response.data
    } catch (error) {
        console.error('Failed to get video from URL:', error)
        throw error
    }
}

export const getOneClickCampaignDetails_api = async ({
    brandId,
    campaignInput
}: {
    brandId: number;
    campaignInput: FacebookCampaignSubmissionType;
}): Promise<any> => {
    try {
        const response = await Axios.post<any>(`/agents/get-one-click-campaign-details`, {
            brandId,
            campaignInput
        })
        return response.data
    } catch (error) {
        console.error('Failed to get one click campaign details:', error)
        return null
    }
}

/** Simulate campaign checkout and handle task consumption */
export const simulateCampaignCheckout_api = async ({
    brandId,
    campaignId
}: {
    brandId: number;
    campaignId: string;
}): Promise<CampaignCheckoutResponse> => {
    try {
        // Create PayPal order
        const paypalOrderResponse = await Axios.post(`/agents/campaign/${campaignId}/paypal-order`, {
            brandId
        })


        // Capture PayPal order using the orderId from the create response
        const orderId = paypalOrderResponse.data.order.id
        console.log('orderId', orderId);
        const captureResponse = await Axios.post(`/agents/campaign/${campaignId}/paypal-capture`, {
            orderId
        })

        console.log('captureResponse', captureResponse);
        return captureResponse.data
    } catch (error) {
        console.error('Failed to simulate campaign checkout:', error)
        throw error
    }
}

/** Submit campaign directly (for pro users with express setup) */
export const submitCampaignDirect_api = async ({
    brandId,
    campaignId
}: {
    brandId: number;
    campaignId: string;
}): Promise<CampaignSubmissionResponse> => {
    try {
        const response = await Axios.post<CampaignSubmissionResponse>(`/agents/campaign/${campaignId}/submit-direct`, {
            brandId
        })
        return response.data
    } catch (error) {
        console.error('Failed to submit campaign directly:', error)
        throw error
    }
}

/** Get recommended tasks for a brand */
export const getRecommendedTasks_api = async (brandId: number): Promise<AgentTaskOptions[]> => {
    try {
        const response = await Axios.post(`/agents/recommended-tasks`, { brandId })
        return response.data
    } catch (error) {
        console.error('Failed to get recommended tasks:', error)
        throw error
    }
}

/** Task pack purchase interfaces */
export interface TaskPackOption {
    size: number;
    pricePerTask: number;
    totalPriceCents: number;
    savings?: string;
    popular?: boolean;
}

export interface TaskPackPurchaseResponse {
    success: boolean;
    clientSecret: string;
    tasksAdded: number;
    newTaskBalance: number;
    message: string;
}

/** Get available task pack options */
export const getTaskPackOptions_api = async (): Promise<TaskPackOption[]> => {
    try {
        const response = await Axios.get<TaskPackOption[]>(`/agents/task-packs/options`)
        return response.data
    } catch (error) {
        console.error('Failed to get task pack options:', error)
        throw error
    }
}

/** Create payment intent for task pack purchase */
export const createTaskPackPayment_api = async ({
    brandId,
    packSize
}: {
    brandId: number;
    packSize: number;
}): Promise<{ clientSecret: string; amount: number }> => {
    try {
        const response = await Axios.post(`/agents/task-packs/create-payment`, {
            brandId,
            packSize
        })
        return response.data
    } catch (error) {
        console.error('Failed to create task pack payment:', error)
        throw error
    }
}

/** Create payment intent for campaign subscription purchase */
export const createCampaignPayment_api = async ({
    brandId,
    planType,
    billingCycle,
    campaignInput
}: {
    brandId: number;
    planType: string;
    billingCycle: 'monthly' | 'annual';
    campaignInput: any;
}): Promise<{ clientSecret: string; amount: number; breakdown: any }> => {
    try {
        const response = await Axios.post(`/agents/campaign-subscription/create-payment`, {
            brandId,
            planType,
            billingCycle,
            campaignInput
        })
        return response.data
    } catch (error) {
        console.error('Failed to create campaign payment:', error)
        throw error
    }
}

/** Complete task pack purchase after payment success */
export const completeTaskPackPurchase_api = async ({
    brandId,
    paymentIntentId,
    packSize
}: {
    brandId: number;
    paymentIntentId: string;
    packSize: number;
}): Promise<TaskPackPurchaseResponse> => {
    try {
        const response = await Axios.post<TaskPackPurchaseResponse>(`/agents/task-packs/complete-purchase`, {
            brandId,
            paymentIntentId,
            packSize
        })
        return response.data
    } catch (error) {
        console.error('Failed to complete task pack purchase:', error)
        throw error
    }
}

/** Content Analysis interfaces */
export interface AnalyzedPost {
    id: string;
    videoUrl: string;
    thumbnailUrl: string;
    caption: string;
    views: number;
    likes: number;
    comments: number;
    shares: number;
    timestamp: string;
    engagement: {
        rate: number;
        score: number;
    };
    insights: {
        hooks: string[];
        contentType: string;
        visualStyle: string;
        topics: string[];
    };
}

export interface ContentRecommendations {
    topPerformingContent: {
        hooks: string[];
        contentTypes: string[];
        visualStyles: string[];
        topics: string[];
    };
    recommendations: {
        category: string;
        suggestion: string;
        reasoning: string;
    }[];
    contentGaps: string[];
    nextSteps: string[];
}

export interface ContentAnalysisResponse {
    success: boolean;
    analysis: {
        analysisId: string;
        username: string;
        profileData: {
            followers: number;
            postsCount: number;
        };
        analyzedPosts: AnalyzedPost[];
        recommendations: ContentRecommendations;
        createdAt: string;
        expiresAt: string;
        totalVideosAnalyzed: number;
    };
    retrievedAt: string;
}

/** Get content analysis results by analysis ID */
export const getContentAnalysis_api = async (analysisId: string): Promise<ContentAnalysisResponse> => {
    try {
        const response = await Axios.get<ContentAnalysisResponse>(`/agents/content-analysis/${analysisId}`)
        return response.data
    } catch (error) {
        console.error('Failed to get content analysis:', error)
        throw error
    }
}

/** Complete campaign subscription purchase after payment success */
export const completeCampaignPayment_api = async ({
    brandId,
    paymentIntentId,
    planType,
    campaignInput
}: {
    brandId: number;
    paymentIntentId: string;
    planType: string;
    campaignInput: any;
}): Promise<{ success: boolean; subscriptionUpdated: boolean; newTier: string; campaign: any; campaignId: string | null; message: string }> => {
    try {
        const response = await Axios.post(`/agents/campaign-subscription/complete-purchase`, {
            brandId,
            paymentIntentId,
            planType,
            campaignInput
        })
        return response.data
    } catch (error) {
        console.error('Failed to complete campaign subscription purchase:', error)
        throw error
    }
}

